/**
 * A toolbar whose actions never fall outside the window (fixes after rc.9 testing): the actions that do not fit
 * move, least important first, into a More (…) menu at the end of the bar. Widths are measured like the tab rows'
 * (useTabOverflow): a ResizeObserver on the bar and on each action, plus window resizes.
 *
 * Each action is drawn once, in the bar or in the menu, as the component it is (its own button and dialog). The
 * menu stays mounted while closed (hidden), so a dialog opened from it stays open after the menu closes.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { overflowItems, sameKeys } from './barOverflow';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

export interface BarAction {
  /** Stable key; also the item's data-action. */
  key: string;
  node: ReactNode;
  /** Higher stays in the bar longer (default 50). */
  priority?: number;
}

/** Width assumed for an action not measured yet (it has never been in the bar). */
const UNMEASURED = 100;

/** Test ids: `${name}-actions` (the bar), `${name}-more` (the menu button) and `${name}-more-menu`. */
export function OverflowBar({ actions, className = '', name, label = 'Actions' }: { actions: BarAction[]; className?: string; name: string; label?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const widths = useRef(new Map<string, number>());
  const latest = useRef(actions);
  latest.current = actions;
  const [hidden, setHidden] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  useDismissOnOutside(moreRef, open, () => setOpen(false));

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    for (const child of el.querySelectorAll<HTMLElement>(':scope > [data-action]')) widths.current.set(child.dataset.action!, child.getBoundingClientRect().width);
    const style = getComputedStyle(el);
    const gap = parseFloat(style.columnGap) || 0;
    const more = moreRef.current?.getBoundingClientRect().width || 34;
    const next = overflowItems(
      latest.current.map((a) => ({ key: a.key, width: widths.current.get(a.key) ?? UNMEASURED, priority: a.priority ?? 50 })),
      el.clientWidth, more, gap,
    );
    setHidden((prev) => (sameKeys(prev, next) ? prev : next));
  }, []);

  const keys = actions.map((a) => a.key).join(' ');
  // Before paint, so an action never shows outside the bar for a frame.
  useLayoutEffect(measure);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    for (const child of el.querySelectorAll(':scope > [data-action]')) ro.observe(child);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure, keys, hidden]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);
  useEffect(() => { if (!hidden.length) setOpen(false); }, [hidden.length]);

  const inMenu = new Set(hidden);
  return (
    <div className={`overflow-bar ${className}`} ref={ref} role="toolbar" aria-label={label} data-testid={`${name}-actions`}>
      {actions.filter((a) => !inMenu.has(a.key)).map((a) => <div key={a.key} className="bar-item" data-action={a.key}>{a.node}</div>)}
      {hidden.length > 0 && (
        <div className="bar-more menu-anchor" ref={moreRef}>
          <button type="button" className="btn ghost sm icon" aria-label="More actions" title="More actions" aria-haspopup="true" aria-expanded={open}
            onClick={() => setOpen(!open)} data-testid={`${name}-more`}>
            <MoreHorizontal size={16} />
          </button>
          {/* Closed, the menu is only hidden (visibility, which also takes it out of the keyboard order and the
              accessibility tree): a dialog an action opened from it stays on screen. */}
          <div className={`bar-more-menu ${open ? 'open' : ''}`} role="group" aria-label="More actions"
            data-testid={`${name}-more-menu`} data-open={open}
            onClick={(e) => {
              const t = e.target as HTMLElement;
              // An action with a menu of its own (Panes) keeps this one open; others close it once they have opened their dialog.
              if (t.closest('.bar-more-menu .bar-item .menu-anchor')) return;
              if (t.closest('button, a')) setOpen(false);
            }}>
            {actions.filter((a) => inMenu.has(a.key)).map((a) => <div key={a.key} className="bar-item" data-action={a.key}>{a.node}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
